import { useRef, useState } from "react"
import { Box, Button, HStack, IconButton, Spinner, Stack } from "@chakra-ui/react"
import { FaArrowLeft, FaArrowUp } from "react-icons/fa"
import { LuRefreshCw } from "react-icons/lu"
import { useQueryClient } from "@tanstack/react-query"
import { Section, Text } from "@/components"
import { useCommissionSummary } from "@/hooks/commissions"
import { CommissionSummary } from "@/types"
import { CommissionsSummaryTable, CommissionsDealsTable } from "./components"

export const CommissionsComponent = () => {
  const { data: summaries = [], isLoading, refetch } = useCommissionSummary()
  const [selectedDm, setSelectedDm] = useState<CommissionSummary | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const queryClient = useQueryClient()
  const sectionRef = useRef<HTMLDivElement>(null)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await queryClient.invalidateQueries({ queryKey: ["commissionSummary"] })
    await queryClient.invalidateQueries({ queryKey: ["commissionRows"] })
    await queryClient.invalidateQueries({ queryKey: ["missingCommissions"] })
    setIsRefreshing(false)
  }

  const handleSelectDm = (dm: CommissionSummary) => {
    setSelectedDm(dm)
    sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleBack = () => {
    setSelectedDm(null)
  }

  const scrollToTop = () => {
    sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  if (isLoading) {
    return (
      <Section h="100vh" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Section>
    )
  }

  return (
    <Section ref={sectionRef} overflowY="auto" h="100vh">
      <HStack mb={2} flexWrap="wrap" gap={2}>
        {selectedDm && (
          <Button size="sm" variant="outline" onClick={handleBack}>
            <FaArrowLeft />
            All DMs
          </Button>
        )}
        <Text fontSize="xl" fontWeight="semibold">
          {selectedDm ? `${selectedDm.dmName} — Commissions` : "Commissions"}
        </Text>
        <Box ml="auto">
          <HStack gap={2}>
            <IconButton
              size="sm"
              variant="outline"
              aria-label="Scroll to top"
              onClick={scrollToTop}
            >
              <FaArrowUp />
            </IconButton>
            <Button size="sm" variant="outline" onClick={handleRefresh} loading={isRefreshing}>
              <LuRefreshCw />
              Refresh
            </Button>
          </HStack>
        </Box>
      </HStack>

      {selectedDm ? (
        <Stack gap={4}>
          {/* DM summary cards */}
          <HStack flexWrap="wrap" gap={4}>
            <SummaryCard label="Total Deals" value={String(selectedDm.totalDeals)} />
            <SummaryCard label="Exited" value={String(selectedDm.exitedDeals)} />
            <SummaryCard label="Live" value={String(selectedDm.liveDeals)} />
            <SummaryCard label="MOIC" value={selectedDm.moic != null ? `${selectedDm.moic.toFixed(2)}x` : "—"} />
            <SummaryCard label="Commission" value={fmt$(selectedDm.cumulativeCalculated)} />
          </HStack>

          <CommissionsDealsTable
            personId={selectedDm.personId!}
            dmName={selectedDm.dmName}
          />
        </Stack>
      ) : (
        <CommissionsSummaryTable
          summaries={summaries}
          onSelectDm={handleSelectDm}
          selectedPersonId={selectedDm ? (selectedDm as CommissionSummary).personId : null}
        />
      )}
    </Section>
  )
}

const fmt$ = (v: number) => {
  if (v < 0) return `($${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const SummaryCard = ({
  label,
  value,
  color,
}: {
  label: string
  value: string
  color?: string
}) => (
  <Stack
    gap={0}
    px={4}
    py={3}
    borderWidth="1px"
    borderColor="border.default"
    borderRadius="md"
    bg="card.bg"
    minW="120px"
  >
    <Text fontSize="xs" color="text.muted">{label}</Text>
    <Text fontSize="md" fontWeight="semibold" color={color}>{value}</Text>
  </Stack>
)
